import React from "react";
import DataGrid from '@dataGrid';
import GridConfig from "./gridConfig";
import { withStyles } from "@material-ui/core";
import styles from "../../styles";
import { postData } from "../../../../../api/componentAPIs";
import { compose } from "recompose";
import { withSnackbar } from "notistack";
import Events from "../../../../../utilities/EventEmitter";
import StatusBar from '@dataGrid/components/StatusBar';
import MESSAGES from "../../../../../appMessages";
import { isEqual } from "lodash/lang";
import AnchorPointGridHeader from "./gridHeader";
import DeleteAnchorPoint from "./deleteAnchorPoint";
import ShowingInProgressPopup from "./showingInProgressPopup";
import {
  getToggleState,
  generateErrorMessage,
  generateSuccessMessage
} from './utils';
import { RMAXLogger } from "../../../../../services/LoggerService/RMAXLogger";
import { isDispatchHeaderActive } from "../../utils";
import { observer, inject } from "mobx-react";
import { autorun } from "mobx";

@inject('CommonStore', 'IBPlanningStore')
@observer
class AnchorPointGrid extends React.Component {
  constructor(props) {
    super(props);
    this.gridConfig = new GridConfig();
    this.isAnyFilterSet = false;
    this.state = {
      filterData: "",
      quickFilterText: "",
      selectedAnchorPoints: [],
      isDeleteAnchorPointPopupOpen: false,
      isAnchorPointSelectOnGrid: false,
      enableAllAnchorPoints: this.isAllAnchorPointsEnabled(),
      isEnableAnchorPointsInProgress: false,
      gridApi: null,
    };
    this.gridContext = {
      disabled: false,
      tooltipTitle: "",
      routesList: this.props.IBPlanningStore.routesList,
    };
  }

  componentDidMount() {
    this.autorunRouteUpdateDispose = autorun(() => {
      if (!isDispatchHeaderActive()) {
        // Skip gridContext update when accessed from IBPlanning header since routes are already loaded in IBPlanningStore
        return;
      }
      if (this.props.IBPlanningStore.routesList.slice()) {
        this.gridContext = {
          ...this.gridContext,
          routesList: this.props.IBPlanningStore.routesList,
        };
      }
    });
  }

  componentWillUnmount() {
    this.autorunRouteUpdateDispose?.();
  }

  componentDidUpdate(prevProps) {
    if (
      !isEqual(this.props.selectedAnchorPoints, prevProps.selectedAnchorPoints)
    ) {
      this.updateSelection();
    }
    if (prevProps.data !== this.props.data) {
      this.setState({
        enableAllAnchorPoints: this.isAllAnchorPointsEnabled()
      })
    }
  }

  updateSelection = () => {
    if (this.state.gridApi) {
      const nodesToSelect = [];
      this.state.gridApi.forEachNode((node) => {
        if (this.props.selectedAnchorPoints.includes(node.data.anchorPointName)) {
          nodesToSelect.push(node);
        }
      });
      if (nodesToSelect.length > 0) {
        this.state.gridApi.deselectAll();
        this.state.gridApi.setNodesSelected({
          nodes: nodesToSelect,
          newValue: true,
        });
        nodesToSelect.forEach((node) => {
          this.state.gridApi.ensureNodeVisible(node);
        });
      }
    }
  };

  onGridReady = ({ api }) => {
    this.setState({ gridApi: api });
  };

  updateQuickFilterText = (value) => {
    this.setState({
      quickFilterText: value,
    });
  };

  handleRouteAssignedChange = (params) => {
    if (params.newValue !== null) {
      const routeString = params.newValue
        .map((route) => route.label)
        .join(", ");
      if (params.oldValue !== routeString) {
        const routeIdString = params.newValue
          .map((route) => route.value)
          .join(", ");
        this.anchorPointBackup = {
          routeAssigned: params.oldValue,
          routeId: params.data.routeId,
        };
        this.updateUI(params, {
          routeAssigned: routeString,
          routeId: routeIdString,
        });
        this.updateAnchorPoint(params);
      } else {
        params.data["routeAssigned"] = params.oldValue;
        this.state.gridApi.refreshCells();
      }
    } else if (params.oldValue !== null) {
      this.anchorPointBackup = {
        routeAssigned: params.oldValue,
        routeId: params.data.routeId,
      };
      this.updateUI(params, { routeAssigned: null, routeId: null });
      this.updateAnchorPoint(params);
    }
  }

  onDataUpdated = (params) => {
    if (params.colDef.field === "routeAssigned") {
      this.handleRouteAssignedChange(params);
    } else if (
      params.colDef.field === "isSelected" &&
      params.oldValue !== params.newValue
    ) {
      this.updateBulkAnchorPoint(params);
    }
  };

  updateUI = (params, updatedData) => {
    if (updatedData) {
      Object.keys(updatedData).forEach((key) => {
        params.data[key] = updatedData[key];
      });
      this.updateGridContext({ disableGridContext: false, tooltipTitle: "" });
    }
  };

  deleteAnchorPoint = () => {
    const data = {
      scenarioId: this.props.scenarioId,
      anchorPointNames: this.state.selectedAnchorPoints.map((anchorPoint) => {
        return anchorPoint.anchorPointName;
      }),
    };
    postData("DeleteAnchorPoint", data).then(
      (response) => {
        if (response && response.status >= 200 && response.status < 400) {
          this.props.updateAnchorPointDataInStore();
          return;
        }
        this.handleDeleteAnchorPointError(data, response.status);
      },
      () => {
        this.handleDeleteAnchorPointError(data);
      }
    );
  };

  handleDeleteAnchorPointError = (data, statusCode) => {
    if (statusCode === 403) {
      return;
    }
    const errorMessage =
      data.anchorPointNames.length > 1 &&
      `Error while deleting anchor point ${data.anchorPointNames[0]}`;
    this.props.enqueueSnackbar(
      `${data.anchorPointNames.length > 1 ? 'Error while deleting anchor points' : errorMessage}`,
      { variant: 'error' }
    );
  };

  // Common method to handle anchor point updates (create/update/enable/disable all)
  updateAnchorPointCommon = ({apiName, payload, params }) => {
    this.updateGridContext({
      disableGridContext: true,
      tooltipTitle: "Please wait for the last update to finish",
    });

    this.setState({
      isEnableAnchorPointsInProgress:true,
    });

    postData(apiName, payload).then(
      (response) => {
        if (response && Math.trunc(response.status / 100) === 2) {
          this.handleUpdateAnchorPointSuccess(params, payload);
          return;
        }
        this.handleUpdateAnchorPointError({params, payload, statusCode: response.status});
      },
      () => {
        this.handleUpdateAnchorPointError({params, payload});
      }
    );

    RMAXLogger.info(apiName, {
      scenarioId: payload?.scenarioId,
      anchorPointCount: payload?.anchorPointNames?.length ?? payload?.anchorPoints?.length ?? 0
    });
  };

  // Filter already enabled/disabled anchor points based on enableAllAnchorPoints
  getFilteredAnchorPointNames = (selectedAnchorPoints, enableAllAnchorPoints) => {
    return selectedAnchorPoints
      .filter((anchorPoint) => anchorPoint.isSelected !== enableAllAnchorPoints)
      .map((anchorPoint) => anchorPoint.anchorPointName);
  };

  // Update bulk anchor point (enable/disable all anchor points)
  updateBulkAnchorPoint = (params) => {
    const { selectedAnchorPoints } = this.state;
    const { data, scenarioId } = this.props;
    const selectedAnchorPointsCount = selectedAnchorPoints.length;
    const totalAnchorPointsCount = data.length;
    const isAnchorPointSelected = selectedAnchorPointsCount > 0;
    const isAllAnchorPointsSelected = totalAnchorPointsCount === selectedAnchorPointsCount;
    let anchorPointNames, isSelected = false;
    if (selectedAnchorPointsCount === 1) {
      // Handle enable/disable single anchor point
      anchorPointNames = selectedAnchorPoints.map((anchorPoint) => anchorPoint.anchorPointName);
      isSelected = Boolean(params.value || params.enableAllAnchorPoints); //Single anchor point can be enabled/disabled from grid toggle present in cell as well as from bulk toggle present in header
    } else if (isAnchorPointSelected && !isAllAnchorPointsSelected) {
      // Handle enable/disable selected anchor points
      anchorPointNames = this.getFilteredAnchorPointNames(selectedAnchorPoints, params.enableAllAnchorPoints);
      isSelected = params.enableAllAnchorPoints;
    } else {
      // Handle enable/disable all anchor points with/without selection
      const payload = {
        scenarioId,
        routeNames: [],
        anchorPointName: null,
        isSelected: params.enableAllAnchorPoints,
        enableAllAnchorPoints: params.enableAllAnchorPoints,
      };
      this.updateAnchorPointCommon({apiName: "UpdateAnchorPoint", payload, params});
      this.setState({ enableAllAnchorPoints: params.enableAllAnchorPoints });
      return;
    }
    const payload = {
      isSelected,
      anchorPointNames,
      scenarioId,
    };
    this.updateAnchorPointCommon({ apiName: 'UpdateBulkAnchorPoint', payload, params });
  };

  // Update single anchor point (create/update)
  updateAnchorPoint = (params) => {
    const payload = {
      scenarioId: this.props.scenarioId,
      anchorPointName: params.data.anchorPointName,
      isSelected: params.data.isSelected,
      routeNames: params.data.routeAssigned ? params.data.routeAssigned.split(', ') : [],
    };
    this.updateAnchorPointCommon({apiName: "UpdateAnchorPoint", payload, params});
  };

  handleUpdateAnchorPointSuccess = (params, payload) => {
    const successMessage = generateSuccessMessage(params, payload);
    if (successMessage) {
      this.props.enqueueSnackbar(successMessage, { variant: "success" });
    }
    
    const {colDef: {field} = {}} = params;
    // Call onDataUpdated for specific field updates
    if (["routeAssigned", "isSelected"].includes(field)) {
      this.props.onDataUpdated();
    }

    if (isDispatchHeaderActive()) {
      this.props.updateAnchorPointDataInStore();
    } else {
      Events.emit("RefreshIBStore", {
        showNotification: false,
        refreshAdditionalData: {fetchAnchorPoint: true}  
      });

    }
    this.setState({
      isEnableAnchorPointsInProgress: false,
    });
    this.updateGridContext({
      disableGridContext: false,
      tooltipTitle: "",
    });
  }

  handleUpdateAnchorPointError = ({ params, payload, statusCode }) => {
    if (statusCode !== 403) {
      const errorMessage = generateErrorMessage(params, payload);
      this.props.enqueueSnackbar(errorMessage, { variant: "error" });
    }
  
    if (params?.enableAllAnchorPoints !== undefined) {
      this.setState((prevState) => ({
        ...prevState,
        enableAllAnchorPoints: this.isAllAnchorPointsEnabled(),
      }));
    } else if (params.colDef?.field === "routeAssigned") {
      this.updateUI(params, this.anchorPointBackup);
    } else if (params.colDef?.field === "isSelected") {
      this.updateUI(params, { isSelected: params.oldValue });
    }
    
    // Reset the progress state and grid context when API fails
    this.setState({
      isEnableAnchorPointsInProgress: false,
    });
    this.updateGridContext({
      disableGridContext: false,
      tooltipTitle: "",
    });
    
    this.props.onDataUpdated();
  };

  onSelectionChanged = () => {
    if (this.props.anchorPointSelectedOnGrid && this.state.gridApi) {
      const selectedAnchorPoints = this.state.gridApi
        .getSelectedRows()
        .map((anchor) => anchor);
      this.props.anchorPointSelectedOnGrid(selectedAnchorPoints);
      this.setState({ selectedAnchorPoints });
    }
  };

  onDoubleClicked = (params) => {
    if(params.colDef.field === "routeAssigned" && !params.data.isSelected){
      this.props.enqueueSnackbar(MESSAGES.ANCHORPOINT.DISABLED_ANCHORPOINT, {variant: "info"});
  }
}
  isAllAnchorPointsEnabled = () => {
    const { totalAnchorPoint, enabledAnchorPoints } = this.getAnchorPointCount(this.props.data);
    return totalAnchorPoint === enabledAnchorPoints
  }

  getAnchorPointCount = (anchorPoints) => {
    let [totalAnchorPoint, enabledAnchorPoints] = [0, 0];
    if (anchorPoints) {
      totalAnchorPoint = anchorPoints.length;
      enabledAnchorPoints = anchorPoints.filter(
        (anchorPoint) => anchorPoint.isSelected
      ).length;
    }
    return {totalAnchorPoint, enabledAnchorPoints };
  };

  doesExternalFilterPass = (node) => {
    switch (this.state.filterData) {
      case "enabledAnchor":
        return Boolean(node.data.isSelected);
      default:
        return true;
    }
  };

  updateGridData = (updatedFilterData) => {
    this.setState(
      {
        filterData: updatedFilterData,
      },
      () => this.state.gridApi.onFilterChanged()
    );
  };

  constructStatusBar = (filteredData) => {
    const { totalAnchorPoint, enabledAnchorPoints } = this.getAnchorPointCount(filteredData);

    const statusBarData = [
      {
        label: totalAnchorPoint + " Anchor Points",
        filterCondition: "",
      },
      {
        label: enabledAnchorPoints + " Enabled",
        filterCondition: "enabledAnchor",
      },
    ];
    return statusBarData;
  };
  deleteAnchorPointPopupClose = () => {
    this.setState({ isDeleteAnchorPointPopupOpen: false });
  };
  deleteAnchorPointPopupOpen = () => {
    this.setState({ isDeleteAnchorPointPopupOpen: true });
  };
  deleteAnchorPointConfirm = () => {
    this.deleteAnchorPoint();
  };
  updateGridContext({ disableGridContext, tooltipTitle }) {
    this.gridContext.disabled = disableGridContext;
    this.gridContext.tooltipTitle = tooltipTitle;
    this.state.gridApi.refreshCells({ force: true });
  }

  onModelUpdated = () => {
    const isQuickFilterPresent = this.state.gridApi.isQuickFilterPresent(); //set when something is searched in searchbar
    const isColumnFilterPresent = this.state.gridApi.isColumnFilterPresent();
    if (
      isColumnFilterPresent ||
      isQuickFilterPresent ||
      this.state.quickFilterText
    ) {
      this.isAnyFilterSet = true;
      const filteredRows = [];
      this.state.gridApi.forEachNodeAfterFilterAndSort(node => {
        filteredRows.push(node.data);
      });
      this.props.CommonStore.updateFilteredAnchorPoints(filteredRows);
    } else {
      if (this.isAnyFilterSet) {
        this.props.CommonStore.updateFilteredAnchorPoints(this.props.data);
        this.isAnyFilterSet = false;
      }
    }
  };

  onCellFocused = (params) => {
    if (this.props.isAddUpdateAnchorPointFormOpen) {
      if (!params.event.ctrlKey || this.state.selectedAnchorPoints.length) {
        this.setState({
          isAnchorPointSelectOnGrid: true,
        });
      }
    }
  };

  cancelAnchorPointSelection = () => {
    this.setState({
      isAnchorPointSelectOnGrid: null,
    });
  };

  render(){
    const { config } = this.gridConfig;
    const { selectedAnchorPoints, enableAllAnchorPoints } = this.state;
    const { classes, error, data } = this.props;
    return (
      <div className={classes.anchorSubModulePart}>
        <div className={classes.anchorPointModuleContainer}>
          <AnchorPointGridHeader
            gridApi = {this.state.gridApi}
            updateQuickFilterText={this.updateQuickFilterText}
            selectedAnchorPoints={selectedAnchorPoints}
            deleteAnchorPointPopupOpen={this.deleteAnchorPointPopupOpen}
            updateAnchorPointOpen={this.props.updateAnchorPointOpen}
            addAnchorPointOpen={this.props.addAnchorPointOpen}
            isToggleEnabled={getToggleState(selectedAnchorPoints, enableAllAnchorPoints)}
            onBulkAnchorPointToggleClicked={this.updateBulkAnchorPoint}
            isAddUpdateAnchorPointFormOpen={
              this.props.isAddUpdateAnchorPointFormOpen
            }
            isEnableAnchorPointsInProgress={
              this.state.isEnableAnchorPointsInProgress
            }
          />
          <div className={`${classes.dataGrid} ${classes.anchorPointContent}`}>
            {!error ? (
              <DataGrid
                columnDefs={config.columns}
                config={config}
                enableFilter={config.enableFilter}
                enableSorting={config.enableSorting}
                rowSelection={config.rowSelection}
                rowDeselection={config.rowDeselection}
                rowData={data}
                quickFilterText={this.state.quickFilterText}
                onGridReady={this.onGridReady}
                onCellValueChanged={this.onDataUpdated}
                onSelectionChanged={this.onSelectionChanged}
                onCellDoubleClicked={this.onDoubleClicked}
                onRowDataUpdated={this.updateSelection}
                suppressRowClickSelection={
                  this.props.isAddUpdateAnchorPointFormOpen
                }
                onCellMouseDown={this.onCellFocused}
                panelLabel={(filteredData) => (
                  <StatusBar
                    data={this.constructStatusBar(filteredData)}
                    filterGridData={this.updateGridData}
                  />
                )}
                isExternalFilterPresent={() => Boolean(this.state.filterData)}
                doesExternalFilterPass={this.doesExternalFilterPass}
                context={this.gridContext}
                getRowId={(params) => params.data.anchorPointName}
                onModelUpdated={this.onModelUpdated}
              />
            ) : (
              <div className={classes.errorMessageContainer}>
                <div className={classes.errorMessage}> {error} </div>
              </div>
            )}
          </div>
        </div>
        {this.state.isDeleteAnchorPointPopupOpen && (
          <DeleteAnchorPoint
            selectedAnchorPoints={selectedAnchorPoints}
            onCancel={this.deleteAnchorPointPopupClose}
            onConfirm={this.deleteAnchorPointConfirm}
          />
        )}
        {this.state.isAnchorPointSelectOnGrid && (
          <ShowingInProgressPopup
            onClose={this.cancelAnchorPointSelection}
            anchorPointsInfo={this.props.anchorPointsInfo}
          />
        )}
      </div>
    );
  }
}
export default compose(withStyles(styles), withSnackbar)(AnchorPointGrid);

